import { useEffect, useState } from 'react';
import { generateComprehensiveApplicationPDF } from '@/utils/pdfGenerator';
import { Button } from '@/components/ui/button';
import { Download, RefreshCw } from 'lucide-react';

const dummyData = {
  email: 'john.smith@example.com',
  phoneNumber: '7912345678',
  countryCode: '+44',
  accountType: 'individual',
  firstName: 'John',
  lastName: 'Smith',
  extractedFirstName: 'John',
  extractedLastName: 'Smith',
  dateOfBirth: '1985-03-15',
  extractedDateOfBirth: '1985-03-15',
  extractedNationality: 'United Kingdom',
  nationality: 'United Kingdom',
  countryOfResidence: 'United Kingdom',
  address: {
    street: '123 Financial Street',
    city: 'London',
    postalCode: 'EC2N 4AY',
    country: 'United Kingdom',
  },
  extractedAddress: {
    street: '45 Banking Avenue, Flat 12',
    city: 'London',
    postalCode: 'SW1A 1AA',
    country: 'United Kingdom',
  },
  documentType: 'passport',
  extractedDocumentNumber: 'GB123456789',
  extractedDocumentCountry: 'United Kingdom',
  extractedDocumentIssueDate: '2020-01-15',
  extractedDocumentExpiry: '2030-01-14',
  employmentStatus: 'employed',
  employerDetails: {
    name: 'Tech Finance Ltd',
    jobTitle: 'Senior Software Engineer',
  },
  industry: 'Technology',
  annualIncome: '£75,000 - £100,000',
  sourceOfFunds: ['salary', 'investments'],
  taxResidencies: [{ name: 'United Kingdom', tin: 'AB123456C' }],
  isUSResident: false,
  isPEP: false,
  pepDetails: '',
  paymentDestinations: [
    { name: 'United Kingdom' },
    { name: 'Germany' },
    { name: 'France' },
    { name: 'United States' },
  ],
  documentSignature: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==',
  documentsSignedAt: new Date().toISOString(),
  idDocumentImageUrl: '/demo/passport-sample.jpg',
  selfieImageUrl: '/demo/selfie-sample.jpg',
  livenessImageUrl: '/demo/liveness-frame.jpg',
  poaImageUrl: '/demo/poa-utility-bill.jpg',
};

export default function PDFPreview() {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const generatePreview = () => {
    setLoading(true);
    try {
      const doc = generateComprehensiveApplicationPDF(dummyData, { includeSignature: true });
      const blob = doc.output('blob');
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    generatePreview();
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, []);

  const handleDownload = () => {
    if (pdfUrl) {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = 'PayNovus_Application_Preview.pdf';
      link.click();
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="bg-[hsl(var(--navy))] text-white p-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold">PDF Preview</h1>
          <p className="text-sm text-white/70">Preview with dummy data</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={generatePreview} className="text-white border-white/30 hover:bg-white/10">
            <RefreshCw className="w-4 h-4 mr-2" />
            Regenerate
          </Button>
          <Button size="sm" onClick={handleDownload} className="bg-accent text-accent-foreground hover:bg-accent/90">
            <Download className="w-4 h-4 mr-2" />
            Download PDF
          </Button>
        </div>
      </div>
      
      <div className="flex-1 p-4">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
          </div>
        ) : pdfUrl ? (
          <iframe
            src={pdfUrl}
            className="w-full h-[calc(100vh-120px)] border rounded-lg shadow-lg"
            title="PDF Preview"
          />
        ) : (
          <div className="flex items-center justify-center h-full text-muted-foreground">
            Failed to generate PDF
          </div>
        )}
      </div>
    </div>
  );
}
